// src/pages/ColorDetail.jsx
import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getColorBySlug } from "../data/colors";

const SHIPPING_OPTIONS = [
  { id: "ground", label: "Ground (3–7 business days)", upcharge: 0 },
  { id: "2day", label: "2-Day Air", upcharge: 80 },
  { id: "overnight", label: "Overnight", upcharge: 150 },
];

export default function ColorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const color = getColorBySlug(slug);

  const [width, setWidth] = React.useState(color?.widths?.[0] || '60"');
  const [length, setLength] = React.useState(color?.lengths?.[0] || "50 ft");
  const [shipping, setShipping] = React.useState("ground");
  const [quantity, setQuantity] = React.useState(1);
  const [added, setAdded] = React.useState(false);

  if (!color) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-14">
        <p className="text-neutral-200 mb-4">Color not found.</p>
        <button
          onClick={() => navigate("/colors")}
          className="px-4 py-2 rounded-xl bg-white text-black text-sm font-semibold"
        >
          Back to Colors
        </button>
      </section>
    );
  }

  const lengthUpcharge = length === "75 ft" ? 200 : 0;
  const shippingDef = SHIPPING_OPTIONS.find((s) => s.id === shipping) || SHIPPING_OPTIONS[0];
  const shippingUpcharge = shippingDef.upcharge;

  const unitPrice = color.basePrice + lengthUpcharge + shippingUpcharge;
  const totalPrice = unitPrice * Math.max(1, Number(quantity) || 1);

  const handleAddToCart = () => {
    const qty = Math.max(1, Number(quantity) || 1);

    const item = {
      id: `${color.slug}-${width}-${length}-${shipping}`,
      slug: color.slug,
      name: color.name,
      width,
      length,
      shipping: shippingDef.label,
      quantity: qty,
      unitPrice,
      totalPrice: unitPrice * qty,
      image: color.thumb,
      type: "wrap-roll",
    };

    try {
      const existingRaw = localStorage.getItem("ulwrapsCart");
      const existing = existingRaw ? JSON.parse(existingRaw) : [];
      existing.push(item);
      localStorage.setItem("ulwrapsCart", JSON.stringify(existing));
      setAdded(true);
      setTimeout(() => setAdded(false), 2500);
    } catch (e) {
      console.error("Failed to write cart", e);
      alert("Could not save to cart. Check browser storage settings.");
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-14">
      <div className="grid md:grid-cols-2 gap-10">
        {/* Image */}
        <div className="rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-950">
          <img
            src={color.image}
            alt={color.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Details */}
        <div>
          <Link
            to="/colors"
            className="text-xs text-neutral-400 hover:text-neutral-200"
          >
            ← Back to all colors
          </Link>

          <h1 className="mt-3 text-3xl font-bold text-white">{color.name}</h1>
          <p className="mt-1 text-sm text-neutral-400">
            {color.brand} • {color.finish} • {color.family}
          </p>

          <p className="mt-4 text-sm text-neutral-300">{color.description}</p>

          {/* Options */}
          <div className="mt-6 space-y-4">
            <Field label="Roll Width">
              <select
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-black"
              >
                {color.widths.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Roll Length">
              <select
                value={length}
                onChange={(e) => setLength(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-black"
              >
                {color.lengths.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-neutral-500 mt-1">
                50 ft is enough for most full wraps. 75 ft is safer for large SUVs / trucks.
              </p>
            </Field>

            <Field label="Shipping">
              <select
                value={shipping}
                onChange={(e) => setShipping(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-black"
              >
                {SHIPPING_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Quantity">
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-24 rounded-xl border border-neutral-300 px-3 py-2 text-black"
              />
            </Field>
          </div>

          {/* Price + Add to Cart */}
          <div className="mt-6 flex flex-col gap-2">
            <div className="text-sm text-neutral-300">
              <div>
                Unit price:{" "}
                <span className="font-semibold text-white">
                  ${unitPrice.toLocaleString()}
                </span>
              </div>
              <div>
                Total ({Math.max(1, Number(quantity) || 1)} roll
                {Math.max(1, Number(quantity) || 1) > 1 ? "s" : ""}):{" "}
                <span className="font-semibold text-white">
                  ${totalPrice.toLocaleString()}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleAddToCart}
              className="mt-3 inline-flex items-center justify-center rounded-xl bg-white text-black px-6 py-3 text-sm font-semibold hover:bg-neutral-200 transition"
            >
              Add to Cart
            </button>

            {added && (
              <div className="text-xs text-emerald-400 mt-1">
                Added to cart. Cart page coming next.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1 text-neutral-100">
        {label}
      </label>
      {children}
    </div>
  );
}
